import type { CallInfo } from '@compass/client/types/call';
import { Check, Link2, Phone } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { FormattedDateTime } from '@/components/FormattedDateTime';
import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/ui/copy-button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { isCaseLinked } from '@/utils/calls';

import { CallHistoryItem } from './CallHistoryItem';

interface LiveSessionPanelProps {
  activeCall: CallInfo | null;
  activeCallLoading: boolean;
  calls: CallInfo[];
  callsLoading: boolean;
  /** ID of the call currently being linked, or null if none */
  linkingCallId: string | null;
  onLinkCall: (call: CallInfo) => void;
  /** Workflow name to display in the link confirmation dialog */
  workflowName?: string;
}

export const LiveSessionPanel: React.FC<LiveSessionPanelProps> = ({
  activeCall,
  activeCallLoading,
  calls,
  callsLoading,
  linkingCallId,
  onLinkCall,
  workflowName,
}) => {
  const [expandedCallId, setExpandedCallId] = useState<string | null>(null);
  const [pendingLinkCall, setPendingLinkCall] = useState<CallInfo | null>(null);

  const handleLinkClick = (call: CallInfo) => {
    if (!workflowName) {
      onLinkCall(call);
      return;
    }
    setPendingLinkCall(call);
  };

  const handleConfirmLink = () => {
    if (pendingLinkCall) {
      onLinkCall(pendingLinkCall);
      setPendingLinkCall(null);
    }
  };

  const handleCancelLink = () => {
    setPendingLinkCall(null);
  };

  // Genesys calls carry conversation_id; older CXOne calls carry contact_id instead.
  // TODO(AQ-712): drop the CXOne branch once no program has active CXOne calls.
  const input = activeCall?.input_data;
  const isGenesys = !!input?.conversation_id;
  const headline = isGenesys
    ? { label: 'Queue', value: input?.queue_name }
    : { label: 'Skill', value: input?.skill_name };
  const idRows = isGenesys
    ? [
        { label: 'Session', copyLabel: 'Session ID', value: input?.session_id },
        { label: 'Conversation', copyLabel: 'Conversation ID', value: input?.conversation_id },
      ]
    : [
        { label: 'Contact', copyLabel: 'Contact ID', value: input?.contact_id },
        {
          label: 'Master contact',
          copyLabel: 'Master Contact ID',
          value: input?.master_contact_id,
        },
      ];
  const startedAt: string | undefined =
    activeCall?.started_at || input?.call_start_time || activeCall?.created_at;

  // Filter out the active call from the history list to avoid duplication
  const historyCalls = useMemo<CallInfo[]>(
    () => (activeCall?.id ? calls.filter(c => c.id !== activeCall.id) : calls),
    [calls, activeCall?.id],
  );

  const handleToggleCall = (callId: string | undefined) => {
    if (!callId) return;
    setExpandedCallId(prev => (prev === callId ? null : callId));
  };

  return (
    <div className="w-[350px] shrink-0 border-r bg-card h-full flex flex-col overflow-hidden">
      {/* Active Call Section */}
      <div className="shrink-0 p-4 border-b">
        <div className="flex items-center gap-2 mb-3">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Live Session
          </h3>
        </div>

        {activeCallLoading ? (
          <div className="flex items-center justify-center py-4">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
          </div>
        ) : activeCall ? (
          <div className="overflow-hidden rounded-xl border bg-card">
            <div className="p-3">
              <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <span className="h-2 w-2 shrink-0 rounded-full bg-success" />
                Active call
              </span>

              <div className="mt-3">
                <p className="text-xs text-muted-foreground">{headline.label}</p>
                <p
                  className="truncate text-base font-semibold text-foreground"
                  title={headline.value ? String(headline.value) : undefined}
                >
                  {headline.value ?? '—'}
                </p>
              </div>

              <div className="my-3 border-t" />

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">Reporter phone</p>
                  <p className="truncate font-medium text-foreground">
                    {input?.reporter_phone ?? '—'}
                  </p>
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">Started</p>
                  <FormattedDateTime value={startedAt} className="font-medium text-foreground" />
                </div>
              </div>
            </div>

            <div className="space-y-1.5 border-t bg-muted/40 px-3 py-2.5">
              {idRows.map(row => (
                <div key={row.label} className="flex items-center gap-2">
                  <span className="w-24 shrink-0 text-xs text-muted-foreground">{row.label}</span>
                  <span
                    className="min-w-0 flex-1 truncate font-mono text-xs text-foreground"
                    title={row.value ? String(row.value) : undefined}
                  >
                    {row.value ?? '—'}
                  </span>
                  {row.value && (
                    <CopyButton
                      value={String(row.value)}
                      label={row.copyLabel}
                      className="shrink-0 rounded-md border bg-background"
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="p-3">
              {isCaseLinked(activeCall) ? (
                <Button className="w-full gap-1.5" disabled size="sm" variant="outline">
                  <Check className="h-4 w-4" />
                  Linked
                </Button>
              ) : (
                <Button
                  onClick={() => handleLinkClick(activeCall)}
                  className="w-full gap-1.5"
                  disabled={!!linkingCallId}
                  size="sm"
                >
                  <Link2 className="h-4 w-4" />
                  {linkingCallId === activeCall?.id ? 'Linking...' : 'Link to case'}
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-dashed p-4 text-center">
            <Phone className="h-5 w-5 mx-auto text-muted-foreground mb-1" />
            <p className="text-sm text-muted-foreground">No active call</p>
          </div>
        )}
      </div>

      {/* Recent Call History Section */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="shrink-0 px-4 pt-4 pb-2">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Recent Call History
          </h3>
        </div>

        <div className="flex-1 overflow-y-auto px-4 pb-4 pt-2 space-y-2">
          {callsLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
            </div>
          ) : historyCalls.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-sm text-muted-foreground">No recent calls</p>
            </div>
          ) : (
            historyCalls.map(call => (
              <CallHistoryItem
                key={call.id}
                call={call}
                isExpanded={expandedCallId === call.id}
                isLinking={linkingCallId === call.id}
                isLinked={isCaseLinked(call)}
                onToggle={() => handleToggleCall(call.id)}
                onLink={handleLinkClick}
              />
            ))
          )}
        </div>
      </div>
      <Dialog open={!!pendingLinkCall} onOpenChange={handleCancelLink}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Link Call to Manufacturer</DialogTitle>
            <DialogDescription>
              Are you sure you want to link this call to{' '}
              <span className="font-semibold text-foreground">{workflowName}</span>?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelLink}>
              Cancel
            </Button>
            <Button onClick={handleConfirmLink}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
